import { useEffect, useRef } from "react";

import { applyImputationDefaults } from "../engine/imputation-defaults.js";

/**
 * Applies `MethodDef` Imputation defaults to form items.
 *
 * For each item in `form.itemGroups[].items` that has:
 *   - `item.methodDef.methodType === 1` (MethodType.Imputation — numeric enum value 1)
 *   - `item.isVisible !== false` — hidden items are skipped, they get a fresh
 *     try when visibility flips on
 *   - a current value in `formData` that is empty (null / undefined / "" / [])
 *   - an expression that evaluates to a non-undefined value with the current
 *     `formData` / `dependencies` / `identifierModel`
 *
 * …evaluates `item.methodDef.expression` via the shared sandbox, then calls
 * both `setValue` (RHF) and `setItemValue` (reducer dispatch) so visibility
 * expressions and other formData-derived state see the default value.
 *
 * Contract:
 *   - Each item's default fires at most once per form-definition. A SET_FORM
 *     (fresh `itemGroups` identity) clears the tracker; a subsequent
 *     SET_ITEM_VALUE / SET_DEPENDENCY does not.
 *   - A default that evaluates to `undefined` (e.g. cross-form dependency
 *     not yet provided) does NOT mark the item as applied — the next render
 *     will retry. Once a non-undefined value is written (or the user types
 *     one manually), the item is locked.
 *   - A hidden item never fires until visibility turns on. This matches
 *     the live designer scenario where the default-bearing item is gated
 *     behind a parent radio.
 *
 * @param {{
 *   form: {
 *     itemGroups: Array<{ items: Array<{ oid: string, isVisible?: boolean, methodDef?: { methodType: number, expression: string } }> }>,
 *     formData: object,
 *     dependencies: object,
 *     identifierModel: object,
 *   },
 *   setValue: (oid: string, value: *) => void,
 *   setItemValue: (oid: string, value: *, format?: *) => void,
 *   readOnly: boolean,
 *   onTypeMismatch?: (mismatch: {
 *     oid: string, label?: string, itemType?: string, expression: string,
 *     expected: string, actual: string, source: "default", message: string,
 *   }) => void,
 * }} params
 */
export function useDefaults({
    form,
    setValue,
    setItemValue,
    readOnly,
    onTypeMismatch,
}) {
    // Per-item flag: defaults have been applied (or short-circuited because
    // the user already typed a value) for this item within the current
    // form-definition. Reset when itemGroups identity changes.
    const appliedItemsRef = useRef(new Set());
    const lastItemGroupsRef = useRef(null);

    // Held in a ref, not listed as an effect dependency: callers pass an inline
    // arrow (the common case), which would otherwise re-run the effect on every
    // render. The per-item lock below already prevents a repeat report, so this
    // is about not re-arming the effect rather than about dedupe.
    const onTypeMismatchRef = useRef(onTypeMismatch);
    onTypeMismatchRef.current = onTypeMismatch;

    useEffect(() => {
        // In readOnly mode (viewing a submitted form) leave stored values
        // as-is — applying an Imputation default would display a value that
        // was never submitted.
        if (readOnly) {
            return;
        }

        if (!form?.itemGroups?.length) {
            appliedItemsRef.current = new Set();
            lastItemGroupsRef.current = null;
            return;
        }

        if (form.itemGroups !== lastItemGroupsRef.current) {
            appliedItemsRef.current = new Set();
            lastItemGroupsRef.current = form.itemGroups;
        }

        // Everything below the scheduling lives in the engine. It is handed
        // the same Set the ref holds and the same callbacks, so the operations
        // and their order are exactly what this effect used to do inline.
        applyImputationDefaults({
            form,
            appliedOids: appliedItemsRef.current,
            setValue,
            setItemValue,
            onTypeMismatch: onTypeMismatchRef.current,
        });
    }, [form, setValue, setItemValue, readOnly]);
}
